import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import type { LeadActivity } from './leadActivity';
import type { CopySet } from './types';
import type { Database } from '../../types/database.types';

export type MessageSubject = { kind: 'prospect' | 'lead' | 'deal'; id: string };
export type MessageTarget =
  | { kind: 'library'; copy: CopySet; slot: number }
  | { kind: 'draft'; name: string; body: string }
  | { kind: 'clear' };

export interface MessageCommand {
  subject: MessageSubject;
  activityId: string;
  expectedCopy: string | null;
  expectedSlot: number | null;
  expectedSelectedAt: string | null;
  expectedVersion: string | null;
  copySetId: string | null;
  slot: number | null;
  revision: string | null;
  name: string | null;
  body: string | null;
  reason: string | null;
  requestId: string;
}

export function messageCommand(
  subject: MessageSubject,
  activity: Pick<
    LeadActivity,
    'id' | 'copy_set_id' | 'message_slot' | 'message_selected_at' | 'message_version'
  >,
  target: MessageTarget,
  reason: string,
  requestId: string,
): MessageCommand {
  if (!subject.id || !activity.id || !requestId)
    throw new Error('Message identity is missing. Reload the record.');
  if (activity.message_selected_at !== null && reason.trim() === '')
    throw new Error('Give a reason for this correction.');
  if (
    target.kind === 'library' &&
    (!target.copy.message_revision ||
      target.copy.archived_at !== null ||
      target.copy.channel !== 'linkedin' ||
      !Number.isInteger(target.slot) ||
      target.slot < 1 ||
      target.slot > 8 ||
      !String(target.copy[`e${target.slot}_body` as keyof CopySet] ?? '').trim())
  ) {
    throw new Error('Choose and preview an active LinkedIn message slot.');
  }
  if (target.kind === 'draft' && (!target.name.trim() || !target.body.trim()))
    throw new Error('Name the draft and paste the message used.');
  return Object.freeze({
    subject: Object.freeze({ ...subject }),
    activityId: activity.id,
    expectedCopy: activity.copy_set_id,
    expectedSlot: activity.message_slot,
    expectedSelectedAt: activity.message_selected_at,
    expectedVersion: activity.message_version,
    copySetId: target.kind === 'library' ? target.copy.id : null,
    slot: target.kind === 'library' ? target.slot : target.kind === 'draft' ? 1 : null,
    revision: target.kind === 'library' ? (target.copy.message_revision ?? null) : null,
    name: target.kind === 'draft' ? target.name.trim() : null,
    body: target.kind === 'draft' ? target.body : null,
    reason: reason.trim() || null,
    requestId,
  });
}

export async function saveMessageSelection(client: SupabaseClient, input: MessageCommand) {
  type Args = Database['public']['Functions']['set_linkedin_message_used']['Args'];
  // PostgreSQL's required arguments are not necessarily NOT NULL. The type
  // generator cannot express this function's explicit-null clear/CAS contract.
  // Check every key against generated Args, then adapt only at the RPC boundary.
  const args = {
    p_subject_kind: input.subject.kind,
    p_subject: input.subject.id,
    p_activity: input.activityId,
    p_expected_copy: input.expectedCopy,
    p_expected_slot: input.expectedSlot,
    p_expected_selected_at: input.expectedSelectedAt,
    p_expected_version: input.expectedVersion,
    p_copy_set: input.copySetId,
    p_slot: input.slot,
    p_target_revision: input.revision,
    p_name: input.name,
    p_body: input.body,
    p_reason: input.reason,
    p_request: input.requestId,
  } satisfies { [Key in keyof Args]: Args[Key] | null };
  const { data, error } = await client.rpc('set_linkedin_message_used', args as Args);
  if (error) throw new Error(error.message);
  if (
    !data ||
    typeof data !== 'object' ||
    Array.isArray(data) ||
    typeof data.event_id !== 'number'
  ) {
    throw new Error('Message selection could not be confirmed. Retry the same save.');
  }
  return data;
}

export function useSaveMessageSelection() {
  const { client } = useAuth();
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (command: MessageCommand) => saveMessageSelection(client, command),
    onSettled: () => {
      void cache.invalidateQueries({ queryKey: ['tracker'] });
      void cache.invalidateQueries({ queryKey: ['lead-activity'] });
      void cache.invalidateQueries({ queryKey: ['journey-history'] });
    },
  });
}

export function messageSelectionError(message: string): string {
  if (message.includes('writes_disabled'))
    return 'Message selection is temporarily unavailable. The logged DM is unchanged.';
  if (message.includes('conflict') || message.includes('not_selectable'))
    return 'This DM or message changed. Close this dialog, reload, and review it again.';
  return 'The message selection was not confirmed. Retry the same save, or close and reload before changing it.';
}
